import React from 'react';
import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { LIST_DISCOUNT_POLICIES_RESOURCE } from 'src/features/discountPolicies/api/useListDiscountPolicies';
import { DISCOUNT_POLICY_RESOURCE } from 'src/features/discountPolicies/api/useGetDiscountPolicy';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import DiscountPolicyFormModal from 'src/features/discountPolicies/components/forms/DiscountPolicyFormModal';
import { DiscountPolicy } from 'src/features/discountPolicies/utils/types';
import { useUpdateDiscountPolicy } from 'src/features/discountPolicies/api/useUpdateDiscountPolicy';

interface UpdateDiscountPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedDiscountPolicy: DiscountPolicy;
    children?: React.ReactNode;
}

function UpdateDiscountPolicyFormModal<TData>({
    selectedDiscountPolicy,
    className,
    semantics,
    children,
}: UpdateDiscountPolicyFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateDiscountPolicy({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_DISCOUNT_POLICIES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [DISCOUNT_POLICY_RESOURCE] });
            toast({
                title: formatMessage(discountPoliciesMessages.updateDiscountPolicySuccess),
                description: formatMessage(discountPoliciesMessages.updateDiscountPolicySuccessDescription),
            });
        },
    });

    return (
        <DiscountPolicyFormModal
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ policyId: selectedDiscountPolicy.id, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(discountPoliciesMessages.editDiscountPolicySubtitle)}
            selectedPolicy={selectedDiscountPolicy}
        >
            {children}
        </DiscountPolicyFormModal>
    );
}

export default UpdateDiscountPolicyFormModal;
